import type { ReactElement } from "react";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { FOCUSED_RAIL_SELECTOR, RAIL_SELECTOR } from "../constants/navigation";

interface KeyboardNavigationContextValue {
  readonly isNavigationMode: boolean;
  readonly isNavigationDisabled: boolean;
  readonly enterNavigationMode: () => void;
  readonly exitNavigationMode: () => void;
  readonly toggleNavigationMode: () => void;
}

const KeyboardNavigationContext = createContext<KeyboardNavigationContextValue | null>(null);

export function useKeyboardNavigation(): KeyboardNavigationContextValue {
  const context = useContext(KeyboardNavigationContext);
  if (!context) {
    throw new Error("useKeyboardNavigation must be used within KeyboardNavigationProvider");
  }
  return context;
}

interface KeyboardNavigationProviderProps {
  readonly children: React.ReactNode;
}

/**
 * Check if keyboard navigation should be disabled for current path
 * Disabled only for admin and provider portals (which use traditional forms/tables)
 */
function useIsNavigationDisabled(): boolean {
  const location = useLocation();
  return location.pathname.startsWith("/admin") || location.pathname.startsWith("/provider");
}

/**
 * Provides global keyboard navigation mode state
 *
 * Navigation mode allows keyboard control of content rails:
 * - Tab: Enter/exit navigation mode
 * - Arrow keys: Navigate cards and rails
 * - Enter/Space/i/w: Actions on focused card
 *
 * Automatically disabled for /admin, /provider, and /home/title/* routes
 */
export function KeyboardNavigationProvider({ children }: KeyboardNavigationProviderProps): ReactElement {
  const [isNavigationMode, setIsNavigationMode] = useState(false);
  const isDisabled = useIsNavigationDisabled();

  /**
   * Exit keyboard navigation mode
   *
   * Clears the navigation mode state, removes visual indicators,
   * and blurs any focused rail to return to normal browsing mode.
   *
   * This is called when:
   * - User presses Escape
   * - User clicks/touches anywhere (via global pointerdown listener)
   * - User navigates to a disabled route (admin/provider)
   * - User programmatically exits (e.g., when leaving Suggested tab)
   */
  const exitNavigationMode = useCallback(() => {
    setIsNavigationMode(false);

    try {
      // Remove data attribute from body
      delete document.body.dataset.navigationMode;

      // Blur any focused rail
      const focusedRail = document.querySelector(FOCUSED_RAIL_SELECTOR) as HTMLElement;
      if (focusedRail) {
        focusedRail.blur();
      }
    } catch (error) {
      // Gracefully degrade if DOM operations fail
      console.warn("Failed to exit navigation mode:", error);
      // State is already updated, so nav mode is functionally exited
    }
  }, []);

  /**
   * Enter keyboard navigation mode
   *
   * Enables keyboard-based rail navigation, sets visual indicators,
   * and focuses the first available rail automatically.
   *
   * Does nothing if navigation is disabled for the current route
   * (admin/provider portals use traditional form navigation).
   *
   * This is called when:
   * - User presses Tab (global shortcut)
   * - Auto-enter on Title page Suggested tab
   * - User programmatically enters (e.g., switching to Suggested tab)
   */
  const enterNavigationMode = useCallback(() => {
    // Don't allow entering navigation mode if disabled
    if (isDisabled) return;

    setIsNavigationMode(true);

    try {
      // Add data attribute to body for CSS styling
      document.body.dataset.navigationMode = "true";

      // Focus the first rail
      const firstRail = document.querySelector(RAIL_SELECTOR) as HTMLElement;
      if (firstRail) {
        // Prevent scroll jump when entering mode
        firstRail.focus({ preventScroll: true });
      }
    } catch (error) {
      // Gracefully degrade if DOM operations fail
      console.warn("Failed to enter navigation mode:", error);
      // State is already updated, so nav mode is functionally active
      // even if visual indicators failed to apply
    }
  }, [isDisabled]);

  // Auto-exit navigation mode when entering disabled routes
  useEffect(() => {
    if (isDisabled && isNavigationMode) {
      exitNavigationMode();
    }
  }, [isDisabled, isNavigationMode, exitNavigationMode]);

  const toggleNavigationMode = useCallback(() => {
    if (isNavigationMode) {
      exitNavigationMode();
    } else {
      enterNavigationMode();
    }
  }, [isNavigationMode, enterNavigationMode, exitNavigationMode]);

  // Exit navigation mode when Escape is pressed
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isNavigationMode) {
        exitNavigationMode();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isNavigationMode, exitNavigationMode]);

  // Exit navigation mode on any pointer interaction (capture phase) so a
  // mouse/touch/pencil press behaves like pressing Escape. Use the
  // pointerdown event which fires earlier than 'click' and is more
  // reliable across devices.
  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      // Only process if navigation mode is active
      if (!isNavigationMode) return;

      try {
        const target = e.target as HTMLElement;

        // CRITICAL: Ignore clicks on interactive elements (buttons, links, inputs)
        // Check both the target itself AND its ancestors up the DOM tree
        // This handles cases where you click an SVG/span inside a button
        if (
          target.matches?.('button, a, input, select, textarea, [role="button"]') ||
          target.closest?.('button, a, input, select, textarea, [role="button"]')
        ) {
          return; // Let the click handler work normally
        }

        // Exit navigation mode for clicks on non-interactive areas
        exitNavigationMode();

        // Prevent rails from receiving focus when we exit nav mode via click
        // This stops the rail from highlighting when you click empty space
        const rail = target.closest?.(RAIL_SELECTOR);
        if (rail) {
          e.preventDefault(); // Prevent default focus behavior on the rail
        }
      } catch (error) {
        // Gracefully handle errors in DOM traversal
        console.warn("Failed to handle pointer event:", error);
      }
    };

    // Use capture phase to ensure we exit before React or native handlers run
    document.addEventListener("pointerdown", handlePointerDown, { capture: true });
    return () => document.removeEventListener("pointerdown", handlePointerDown, { capture: true });
  }, [isNavigationMode, exitNavigationMode]);

  const value: KeyboardNavigationContextValue = useMemo(
    () => ({
      isNavigationMode,
      isNavigationDisabled: isDisabled,
      enterNavigationMode,
      exitNavigationMode,
      toggleNavigationMode,
    }),
    [isNavigationMode, isDisabled, enterNavigationMode, exitNavigationMode, toggleNavigationMode],
  );

  return <KeyboardNavigationContext.Provider value={value}>{children}</KeyboardNavigationContext.Provider>;
}
